import {
    ATTRIBUTES,
    getBisProvider,
    openBackInStockModal,
    PUB_SUB_EVENTS,
    SELECTORS,
    createATCitems,
    publish,
    openPersonalizationPopup
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { SizeGuide } from '@/scripts/theme/size-guide'
import { SizePopupDialogue } from '@/scripts/product/size-popup-dialogue'
import {
    applyStoqPreorderProperties,
	getStoqShippingTextInputValue,
	resolveStoqVariantState,
	syncResolvedStoqVariantSelection,
    syncStoqSellingPlanInput,
	syncStoqShippingTextInput,
} from '@/scripts/product/stoq-selling-plan'
import {
	clearScheduledSubmitButtonLabelSync,
	scheduleSubmitButtonLabelSync,
	type SubmitButtonState,
} from '@/scripts/product/submit-button-label-sync'
import {
    addItemsToCart,
    getCartAttributesFromFormData,
    getDOMCartSectionApiIds,
    renderResponseToCartDrawer
} from '@/scripts/core/cart-functions'

type ProductUpsellVariantConfig = {
	id?: string
	handle?: string
	available?: boolean
}

export class ProductStickyAtc extends UcoastEl {
    static htmlSelector = 'product-sticky-atc'
    static selectors = {
        form: 'form',
        submitButton: '.product-sticky-atc__submit-button',
        formIdEl: '[name="id"]',
        variantLabel: '.product-sticky-atc__variant-label',
        variantItems: '.product-sticky-atc__variant-button',
        variantDropdown: '.product-sticky-atc__variant-dropdown',
        genderInputs: '.product-sticky-atc-gender-input',
		triggerPersonalizationPopup: '.trigger-personalization-popup',
        toggleSizeGuide: '.open-size-guide',
        upsellCheckbox: '[data-uc-product-upsell-checkbox]',
        upsellLineInput: '[data-uc-product-upsell-line-input]',
        upsellQuantityInput: '[data-uc-product-upsell-quantity]',
        upsellParentIdInput: '[data-uc-product-upsell-parent-id]',
        upsellVariantIdInput: '[data-uc-product-upsell-variant-id]',
        upsellHandleInput: '[data-uc-product-upsell-handle]',
        upsellVariantMap: '[data-uc-product-upsell-variants]',
    }

    form: HTMLFormElement
    formIdEl: HTMLInputElement
    submitButton: HTMLButtonElement
    variantLabel: HTMLButtonElement
    variantItems: NodeListOf<HTMLButtonElement>
    genderInputs: NodeListOf<HTMLInputElement>
    variantDropdown: HTMLElement
    triggerPersonalizationButton?: HTMLButtonElement
    upsellCheckbox?: HTMLInputElement
    upsellLineInputs: HTMLInputElement[] = []
    upsellQuantityInput?: HTMLInputElement
    upsellParentIdInputs: HTMLInputElement[] = []
    upsellVariantIdInputs: HTMLInputElement[] = []
    upsellHandleInputs: HTMLInputElement[] = []
    hideErrors: boolean
    error?: boolean
    lastSection!: HTMLElement
    toggleSizeGuide: HTMLButtonElement
    private onBodyClickBound: (e: Event) => void
    private onScrollBound?: () => void
    private _scrollRafPending = false
    private _isRestoring = false

    constructor() {
        super()

        // Bind once so we can add/remove reliably during lifecycle
        this.onBodyClickBound = this.onClickBody.bind(this)

        this.form = q.rs(ProductStickyAtc.selectors.form, this)
        this.formIdEl = (this.form.querySelector('[name="id"]') as HTMLInputElement) || (this.form.querySelector('[name="items[0][id]"]') as HTMLInputElement)
        this.submitButton = q.rs(ProductStickyAtc.selectors.submitButton, this)
        this.variantLabel = q.rs(ProductStickyAtc.selectors.variantLabel, this)
        this.variantItems = q.rl(ProductStickyAtc.selectors.variantItems, this)
        this.variantDropdown = q.rs(ProductStickyAtc.selectors.variantDropdown, this)
        this.genderInputs = q.rl(ProductStickyAtc.selectors.genderInputs, this)
        this.triggerPersonalizationButton = this.querySelector(ProductStickyAtc.selectors.triggerPersonalizationPopup) as HTMLButtonElement
        this.toggleSizeGuide = q.rs(ProductStickyAtc.selectors.toggleSizeGuide, this)
        this.upsellCheckbox = document.querySelector<HTMLInputElement>(ProductStickyAtc.selectors.upsellCheckbox) ?? undefined
        this.upsellLineInputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(ProductStickyAtc.selectors.upsellLineInput))
        this.upsellQuantityInput = this.form.querySelector<HTMLInputElement>(ProductStickyAtc.selectors.upsellQuantityInput) ?? undefined
        this.upsellParentIdInputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(ProductStickyAtc.selectors.upsellParentIdInput))
        const hasMultipleVariants = !this.variantDropdown.classList.contains('hidden')
        if (hasMultipleVariants) {
            this.variantItems.forEach((item) => {
                item.classList.remove('active')
            })
            // Keep variant id input disabled until a size is explicitly selected
            this.formIdEl.disabled = true
            // Prevent ATC until valid size selection
            this.submitButton.setAttribute('aria-disabled', 'true')
            // Prevent personalization until valid size selection
            if (this.triggerPersonalizationButton) {
                this.triggerPersonalizationButton.setAttribute('disabled', 'true')
                this.triggerPersonalizationButton.setAttribute('aria-disabled', 'true')
            }
        } else {
            this.formIdEl.disabled = false
        }

        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this))
        this.variantLabel.addEventListener('click', this.toggleVariantDropdown.bind(this))
        this.variantItems.forEach(item => {
            item.addEventListener('click', this.onClickVariantItem.bind(this))
        })

        this.genderInputs.forEach(input => {
            input.addEventListener('change', this.onChangeGender.bind(this))
        })
        this.upsellCheckbox?.addEventListener('change', this.syncUpsellLineItemState.bind(this))

        this.toggleSizeGuide.addEventListener('click', (e: Event) => {
            e.preventDefault()
            const sizeGuide = document.querySelector('size-guide') as SizeGuide
            if(sizeGuide) sizeGuide.open()
        })

        if(this.triggerPersonalizationButton) this.triggerPersonalizationButton.addEventListener('click', this.triggerPersonalization.bind(this))
        this.hideErrors = this.dataset.hideErrors === 'true'
        this.syncUpsellLineItemState()
        
        // Restore saved gender and size selections after component initialization
        this.restoreSavedSelections()
        if (!this.formIdEl.disabled) {
            syncStoqSellingPlanInput({
                form: this.form,
                variantId: this.formIdEl.value,
                preorder: this.submitButton.dataset.preorder == 'true',
            })
			syncStoqShippingTextInput({
				form: this.form,
				preorder: this.submitButton.dataset.preorder == 'true',
				shippingText: getStoqShippingTextInputValue(this.form),
			})
			scheduleSubmitButtonLabelSync(
				this.submitButton,
				() => this.getSubmitButtonState()
			)
        }
    }

	private getSubmitButtonState(): SubmitButtonState {
		const activeVariantButton = q.os<HTMLButtonElement>(
			'.product-sticky-atc__variant-button.active',
			this
		)
		const fallbackLabel =
			q.os<HTMLElement>('[data-uc-inventory-status-text]', this.submitButton)
				?.textContent?.trim() || ''

		return {
			...resolveStoqVariantState({
				variantId: activeVariantButton?.dataset.id,
				available: activeVariantButton?.dataset.available !== 'false',
				preorder: activeVariantButton?.dataset.inventoryStatus === 'PreOrder',
				inventoryStatus: activeVariantButton?.dataset.inventoryStatus || '',
				inventoryStatusOverride:
					activeVariantButton?.dataset.inventoryStatusOverride || '',
				inventoryStatusText:
					activeVariantButton?.dataset.inventoryStatusText || fallbackLabel,
				liquidSellingPlanId: activeVariantButton?.dataset.sellingPlanId || '',
			}),
			priceInCents: activeVariantButton?.dataset.price || null,
		}
	}

    override connectedCallback(): void {
        // Manage global listener lifecycle to avoid duplicates
        document.body.addEventListener('click', this.onBodyClickBound)
        const target = q.rs('.product', document)
        const footer = q.rs('.shopify-section-group-footer-group', document)

        const updateVisibility = () => {
            this._scrollRafPending = false
            this.classList.toggle('active', ((window.innerWidth >= 750 && target.getBoundingClientRect().bottom <= 500 || window.innerWidth < 750)) && footer.getBoundingClientRect().top > footer.offsetHeight)
        }

        this.onScrollBound = () => {
            if (this._scrollRafPending) return
            this._scrollRafPending = true
            requestAnimationFrame(updateVisibility)
        }
        window.addEventListener('scroll', this.onScrollBound, { passive: true })
        updateVisibility()
    }

    override disconnectedCallback(): void {
        document.body.removeEventListener('click', this.onBodyClickBound)
		if (this.onScrollBound) {
			window.removeEventListener('scroll', this.onScrollBound)
		}
		clearScheduledSubmitButtonLabelSync(this.submitButton)
		super.disconnectedCallback()
    }

    triggerPersonalization(e: Event) {
        e.preventDefault()
        // Guard: do nothing if disabled or no explicit size selected
        const target = e.currentTarget as HTMLElement
		if(!target) return
        if (this.formIdEl.disabled || this.triggerPersonalizationButton?.getAttribute('aria-disabled') === 'true' || target.classList.contains('disabled')) return
        const id = String(this.formIdEl.value)
        const productUrl = String(this.dataset.url)
        openPersonalizationPopup(productUrl, id)
    }

    getUpsellVariantConfig(): ProductUpsellVariantConfig | undefined {
        const activeVariantButton = q.os<HTMLButtonElement>(
            '.product-sticky-atc__variant-button.active',
            this
        )
        if (activeVariantButton?.dataset.upsellVariantId) {
            return {
                id: activeVariantButton.dataset.upsellVariantId,
                handle: activeVariantButton.dataset.upsellProductHandle,
                available: true,
            }
        }

        const mapEl = document.querySelector<HTMLScriptElement>(
            ProductStickyAtc.selectors.upsellVariantMap
        )
        if (!mapEl?.textContent) return undefined

        try {
            const data = JSON.parse(mapEl.textContent) as Record<string, ProductUpsellVariantConfig>
            return data[this.formIdEl.value]
        } catch (error) {
            console.error('Unable to parse product upsell variant map', error)
            return undefined
        }
    }

    syncUpsellVariantState() {
        this.upsellVariantIdInputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(ProductStickyAtc.selectors.upsellVariantIdInput))
        this.upsellHandleInputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(ProductStickyAtc.selectors.upsellHandleInput))

        const config = this.getUpsellVariantConfig()
        const hasVariantMap = Boolean(document.querySelector(ProductStickyAtc.selectors.upsellVariantMap))
        const upsellAvailable = config ? config.available !== false && Boolean(config.id) : !hasVariantMap

        if (config?.id) {
            for (const input of this.upsellVariantIdInputs) {
                input.value = config.id
            }
        }

        if (config?.handle) {
            for (const input of this.upsellHandleInputs) {
                input.value = config.handle
            }
        }

        if (this.upsellCheckbox) {
            this.upsellCheckbox.disabled = !upsellAvailable
            if (!upsellAvailable) this.upsellCheckbox.checked = false
        }

        return upsellAvailable
    }

    syncUpsellLineItemState() {
        this.upsellCheckbox = document.querySelector<HTMLInputElement>(ProductStickyAtc.selectors.upsellCheckbox) ?? undefined
        this.upsellLineInputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(ProductStickyAtc.selectors.upsellLineInput))
        this.upsellQuantityInput = this.form.querySelector<HTMLInputElement>(ProductStickyAtc.selectors.upsellQuantityInput) ?? undefined
        this.upsellParentIdInputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(ProductStickyAtc.selectors.upsellParentIdInput))
        const upsellAvailable = this.syncUpsellVariantState()
        for (const input of this.upsellParentIdInputs) {
            input.value = this.formIdEl.value
        }
        if (this.upsellQuantityInput) this.upsellQuantityInput.value = '1'
        if (!this.upsellCheckbox) return
        const upsellSelected = this.upsellCheckbox.checked && upsellAvailable
        for (const input of this.upsellLineInputs) {
            input.disabled = !upsellSelected
        }
    }

    onChangeGender(e: Event) {
        e.preventDefault()
        const target = e.currentTarget as HTMLInputElement
        if(!target) return
        const selectedGender = target.value
        this.variantItems.forEach(item => {
            item.querySelectorAll('[data-gender]').forEach(_item => {
                const el = _item as HTMLElement
                el.classList.toggle('hidden', el.dataset.gender != selectedGender)
            })
        })
        
        // Update the label text if a size is already selected
        const activeVariantButton = q.os<HTMLButtonElement>('.product-sticky-atc__variant-button.active', this)
        if (activeVariantButton) {
            const labelText = q.rs('[data-uc-text]', this.variantLabel) as HTMLElement
            if (labelText) {
                const genderSpecificSpan = q.os<HTMLElement>(`[data-gender="${selectedGender}"]:not(.hidden)`, activeVariantButton)
                if (genderSpecificSpan && genderSpecificSpan.textContent) {
                    labelText.textContent = genderSpecificSpan.textContent.trim()
                } else {
                    // Fallback to base value if no gender-specific text found
                    const baseValue = activeVariantButton.dataset.value
                    if (baseValue) labelText.textContent = baseValue
                }
            }
        }
        
        // Save gender selection (preserve existing variant selection if any)
        const currentVariantId = activeVariantButton?.dataset.id || null
        const currentSizeDisplayName = activeVariantButton?.dataset.value || null
        this.saveSelections(selectedGender, currentVariantId, currentSizeDisplayName)
    }

    onClickBody(e: Event) {
        const target = e.target as HTMLElement
        if(!target) return

        if(!target.closest('.product-sticky-atc__variant-dropdown')) this.variantDropdown.classList.remove('active')
    }

    toggleVariantDropdown(e: Event) {
        e.preventDefault()
        if(!this.variantDropdown) return
        this.variantDropdown.classList.toggle('active')
    }

    onClickVariantItem(e: Event) {
        e.preventDefault()
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        if (!this._isRestoring) {
            this.toggleVariantDropdown(e)
        }

        const resolvedState = resolveStoqVariantState({
            variantId: target.dataset.id,
            available: target.dataset.available == 'true',
            preorder: (target.dataset.inventoryStatus || '') == 'PreOrder',
            inventoryStatus: target.dataset.inventoryStatus || '',
            inventoryStatusOverride: target.dataset.inventoryStatusOverride || '',
            inventoryStatusText: target.dataset.inventoryStatusText || '',
            liquidSellingPlanId: target.dataset.sellingPlanId || '',
        })
        const id = target.dataset.id
        const value = target.dataset.value
        const price = target.dataset.price
        if(!id || !value || !price) return

        // Save size selection
        const selectedGenderInput = q.os<HTMLInputElement>('.product-sticky-atc-gender-input:checked', this)
        const savedGender = selectedGenderInput?.value || null
        this.saveSelections(savedGender, id, value)

        this.formIdEl.value = id
        syncResolvedStoqVariantSelection({
            form: this.form,
            submitButton: this.submitButton,
            variantId: id,
            resolvedState,
            fallbackShippingText: target.dataset.shippingText || '',
            oosPopupUrl: `${this.dataset.url}?variant=${id}&section_id=waitlist`,
			bisProvider: getBisProvider(this.submitButton),
		})
        const labelText = q.rs('[data-uc-text]', this.variantLabel) as HTMLElement
        if(labelText) {
            const selectedGender = savedGender
            
            // Try to find the visible gender-specific size text
            let displayText = value // fallback to base value
            if (selectedGender) {
                const genderSpecificSpan = q.os<HTMLElement>(`[data-gender="${selectedGender}"]:not(.hidden)`, target)
                if (genderSpecificSpan && genderSpecificSpan.textContent) {
                    displayText = genderSpecificSpan.textContent.trim()
                }
            }
            
            labelText.textContent = displayText
        }

        this.variantItems.forEach(item => {
            item.classList.remove('active')
        })
        target.classList.add('active')

        // Enable variant id and actions now that a size has been explicitly selected
        this.formIdEl.disabled = false
        this.syncUpsellLineItemState()
        this.submitButton.removeAttribute('aria-disabled')
        if (this.triggerPersonalizationButton) {
            this.triggerPersonalizationButton.removeAttribute('disabled')
            this.triggerPersonalizationButton.removeAttribute('aria-disabled')
        }

		scheduleSubmitButtonLabelSync(
			this.submitButton,
			() => this.getSubmitButtonState()
		)

        this.variantLabel.querySelector('.product-sticky-atc__variant-label__first')?.classList.add('hidden')
        this.variantLabel.querySelector('.product-sticky-atc__variant-label__second')?.classList.remove('hidden')

        const sizePopupDialogue = SizePopupDialogue.findForSection(this)
        if (target.dataset.sizePopup === 'true') {
            if (sizePopupDialogue && target) {
                sizePopupDialogue.show(target)
            }
        } else {
            sizePopupDialogue?.close()
        }

        if(window.innerWidth < 750) {
            const personalizationBadge = document.querySelector('product-info .price__badge-personalization') as HTMLElement
            if(personalizationBadge) {
                personalizationBadge.dataset.variantId = this.formIdEl.value
            }

            const personalizationTriggers = document.querySelectorAll<HTMLElement>('section[id^="MainProduct-"] .trigger-personalization-popup')
            personalizationTriggers.forEach(trigger => {
                trigger.classList.toggle('disabled', !resolvedState.available)
            })
        }
    }

    getSpinner(target: HTMLButtonElement) {
        return q.rs(SELECTORS.loadingOverlaySpinner, target)
    }

    onSubmitHandler(event: Event) {
        event.preventDefault()
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return
        const bisVariantId = q.oa(this.submitButton, 'data-uc-bis-variant-id')
        const oosUrl = q.oa(this.submitButton, 'data-oos-popup-trigger')
        if (bisVariantId) {
            openBackInStockModal({
                opener: this.submitButton,
                klaviyoUrl: oosUrl,
                variantId: bisVariantId,
            })
            return
        }

        const preorder = this.submitButton.dataset.preorder == 'true'
        this.syncUpsellLineItemState()
        syncStoqSellingPlanInput({
            form: this.form,
            variantId: this.formIdEl.value,
            preorder,
        })
        this.submitButton.setAttribute('aria-disabled', 'true')
        this.submitButton.setAttribute(ATTRIBUTES.loading, '')
        this.getSpinner(this.submitButton).classList.remove('hidden')

        const formData = new FormData(this.form)
        let formVariantId = formData.get('id') || formData.get('items[0][id]')
        if (!formVariantId) throw Error('No variant id found')
        const addedVariantId = parseInt(formVariantId.toString())
        
        type AddToCartItem = {
            id: number
            quantity: number
            selling_plan?: number
            properties: Record<string, string>
            parent_id?: number
        }
        let items: AddToCartItem[] = []
        if(formData.get('items[0][id]')) {
            applyStoqPreorderProperties({
                formData,
                preorder,
                shippingText: getStoqShippingTextInputValue(this.form),
            })
            items = createATCitems(formData) as AddToCartItem[]
        } else {
            applyStoqPreorderProperties({
                formData,
                preorder,
                shippingText: getStoqShippingTextInputValue(this.form),
            })
        }
        const cartAttributes = getCartAttributesFromFormData(formData)

        addItemsToCart(
            items.length ? items : formData,
            getDOMCartSectionApiIds(),
            cartAttributes
        )
            .then((cart) => {
                if ('status' in cart) {
                    return
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: addedVariantId,
                })
                renderResponseToCartDrawer(cart)
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                this.submitButton.removeAttribute(ATTRIBUTES.loading)

                if (!this.error)
                    this.submitButton.removeAttribute('aria-disabled')
                this.getSpinner(this.submitButton).classList.add('hidden')
            })
    }

    private saveSelections(gender: string | null, variantId: string | null, sizeDisplayName: string | null) {
        const state = {
            gender,
            variantId,
            sizeDisplayName,
            productUrl: this.dataset.url || ''
        }
        sessionStorage.setItem('productStickyAtcState', JSON.stringify(state))
    }

    restoreSelections(savedGender: string | null, savedVariantId: string | null, savedSizeDisplayName: string | null) {
        // Always restore gender selection if it was saved
        if (savedGender) {
            const genderInput = q.os<HTMLInputElement>(`.product-sticky-atc-gender-input[value="${savedGender}"]`, this)
            if (genderInput) {
                genderInput.checked = true
                // Update size text visibility without triggering save
                this.variantItems.forEach(item => {
                    item.querySelectorAll('[data-gender]').forEach(_item => {
                        const el = _item as HTMLElement
                        el.classList.toggle('hidden', el.dataset.gender != savedGender)
                    })
                })
            }
        }

        // Only restore size selection if a size was actually selected before
        // This ensures ATC stays disabled if no size was selected
        if (savedVariantId || savedSizeDisplayName) {
            // Use requestAnimationFrame to ensure DOM updates from gender change are complete
            requestAnimationFrame(() => {
                // First try to find by variant ID
                let variantButton: HTMLButtonElement | null = null
                
                if (savedVariantId) {
                    variantButton = q.os<HTMLButtonElement>(`.product-sticky-atc__variant-button[data-id="${savedVariantId}"]`, this) || null
                }
                
                // If variant ID not found, try to match by display name
                if (!variantButton && savedSizeDisplayName) {
                    const allVariantButtons = q.ol<HTMLButtonElement>('.product-sticky-atc__variant-button', this)
                    if (allVariantButtons) {
                        for (const button of allVariantButtons) {
                            const value = button.dataset.value
                            if (!value) continue
                            
                            // Check if the size text matches (considering gender-specific text)
                            const sizeTextEls = q.ol<HTMLElement>('[data-gender]', button)
                            let matches = false
                            
                            if (!sizeTextEls || sizeTextEls.length === 0) {
                                // No gender-specific text, check the button's value directly
                                if (value === savedSizeDisplayName) {
                                    matches = true
                                }
                            } else {
                                // Check gender-specific text
                                for (const textEl of sizeTextEls) {
                                    const textContent = textEl.textContent?.trim()
                                    const isVisible = !textEl.classList.contains('hidden')
                                    const isMatchingGender = savedGender ? textEl.dataset.gender === savedGender : true
                                    
                                    if (textContent === savedSizeDisplayName && isVisible && isMatchingGender) {
                                        matches = true
                                        break
                                    }
                                }
                            }
                            
                            if (matches) {
                                variantButton = button
                                break
                            }
                        }
                    }
                }
                
                // Select the variant if found - this will enable the ATC button
                if (variantButton) {
                    // Use flag to prevent dropdown from toggling open during restore
                    this._isRestoring = true
                    variantButton.click()
                    this._isRestoring = false
                }
                // If variant not found, ATC remains disabled (as initialized)
            })
        }
        // If no size was saved, ATC stays disabled (as initialized in constructor)
    }

    private restoreSavedSelections() {
        try {
            const savedStateStr = sessionStorage.getItem('productStickyAtcState')
            if (!savedStateStr) return
            
            const savedState = JSON.parse(savedStateStr)
            // Only restore if it's for the same product URL
            if (savedState.productUrl !== this.dataset.url) return

            const savedGender = savedState.gender
            const savedVariantId = savedState.variantId
            const savedSizeDisplayName = savedState.sizeDisplayName

            // Use the public restoreSelections method
            this.restoreSelections(savedGender, savedVariantId, savedSizeDisplayName)
        } catch (e) {
            // Silently fail if there's an error parsing saved state
            console.error('Error restoring sticky ATC selections:', e)
        }
    }
}
